import { useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    ChevronRight,
    Download,
    Eye,
    FileText,
    Loader2,
    Pencil,
    RefreshCw,
    Replace as ReplaceIcon,
    Trash2,
    X,
} from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";

import type { OutputTemplateFileType, OutputTemplateVersion } from "@/api";
import { usePanelEscape } from "@/components/frontend/documents/usePanelEscape";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/auth/useAuth";
import { cn } from "@/lib/utils";

import { getDocumentTypePill } from "../../utils/libraryItemTypes";
import { DeleteTemplateDialog } from "./DeleteTemplateDialog";
import { RenameTemplateDialog } from "./RenameTemplateDialog";
import { TemplatePdfViewer } from "./TemplatePdfViewer";
import { UploadTemplateDialog } from "./UploadTemplateDialog";
import {
    latestVersion,
    useOutputTemplateDetail,
    useRetryCompile,
} from "./useOutputTemplates";
import { useOpenOutputTemplateOriginal } from "./useOpenOutputTemplateOriginal";

interface TemplateDetailPanelProps {
    open: boolean;
    onClose: () => void;
    templateId: number;
    isMobile: boolean;
}

export function TemplateDetailPanel({
    open,
    onClose,
    templateId,
    isMobile,
}: TemplateDetailPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const [renameOpen, setRenameOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [replaceOpen, setReplaceOpen] = useState(false);

    const detailQuery = useOutputTemplateDetail(templateId, { enabled: open });
    const retry = useRetryCompile();

    const originalFile = useOpenOutputTemplateOriginal(templateId);

    usePanelEscape({
        enabled: open && !isMobile,
        onClose,
        isModalOpen: false,
        isInlineEditing: false,
        targetRef: panelRef,
    });

    const template = detailQuery.data;
    const statusVersion = useMemo(() => latestVersion(template), [template]);
    const previewSlots = useMemo(
        () => template?.preview?.slot_targets ?? [],
        [template?.preview]
    );
    const fillSlots = useMemo(
        () => previewSlots.filter((s) => s.role !== "exclude"),
        [previewSlots]
    );
    const pdfUrl = template?.preview?.pdf_url ?? null;
    const showPdfViewer =
        template?.file_type === "pdf" && !!pdfUrl && previewSlots.length > 0;

    const panelContent = (
        <div
            ref={panelRef}
            tabIndex={-1}
            data-testid="template-detail-panel"
            className={cn(
                "bg-background @container flex flex-col outline-none",
                // The desktop split-pane column is max-h-[100cqh] (not a definite
                // height), so `h-full` here resolves to CONTENT height and the fill-area
                // viewer would push the panel past the column → the column's own
                // scrollbar. Pin the panel to the container height so the flex chain
                // has a definite ceiling and only the viewer scrolls. Other states
                // (mobile Sheet, no-viewer) keep h-full — the column scrolls as before.
                showPdfViewer && !isMobile ? "h-[100cqh]" : "h-full"
            )}
        >
            <div className="border-border flex shrink-0 items-center gap-3 border-b px-4 py-2.5">
                <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
                    <FileText className="size-4" strokeWidth={2.2} />
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-1">
                    {detailQuery.isLoading ? (
                        <Skeleton className="h-4 w-48" />
                    ) : template ? (
                        <h2
                            title={template.name}
                            className="line-clamp-2 text-sm leading-snug font-semibold"
                        >
                            {template.name}
                        </h2>
                    ) : null}
                    {template && (
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label="Rename template"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => setRenameOpen(true)}
                        >
                            <Pencil className="size-3.5" />
                        </Button>
                    )}
                </div>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close panel"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={onClose}
                >
                    <X className="size-4" />
                </Button>
            </div>

            <div
                className={cn(
                    "flex flex-1 flex-col px-5 py-4",
                    // A shown Preview owns the remaining height and scrolls its viewer
                    // internally, so the body itself must not scroll (a second
                    // scrollbar on top of the outer 100cqh column). Every other
                    // state keeps the plain scrolling body.
                    showPdfViewer ? "min-h-0" : "overflow-y-auto"
                )}
            >
                {detailQuery.isLoading ? (
                    <div className="flex flex-col gap-3">
                        <Skeleton className="h-4 w-56" />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                ) : detailQuery.isError ? (
                    <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
                        Couldn&apos;t load this template.{" "}
                        <button
                            type="button"
                            className="text-primary underline underline-offset-2"
                            onClick={() => detailQuery.refetch()}
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    template && (
                        <>
                            <MetaStrip
                                fileType={template.file_type}
                                createdAt={template.created_at}
                                boundAgentNames={template.bound_agent_names}
                            />

                            <PanelActions
                                fileType={template.file_type}
                                onOpen={originalFile.openOriginal}
                                pending={originalFile.isPending}
                                onReplace={() => setReplaceOpen(true)}
                                onDelete={() => setDeleteOpen(true)}
                            />

                            <CompileStatusBlock
                                version={statusVersion}
                                onRetry={() => retry.mutate(templateId)}
                                onReplace={() => setReplaceOpen(true)}
                                retrying={retry.isPending}
                            />

                            {showPdfViewer && (
                                <>
                                    <Separator className="mb-5 opacity-60" />
                                    <section
                                        data-testid="template-detail-viewer"
                                        className="flex min-h-0 flex-1 flex-col"
                                    >
                                        <h3 className="text-muted-foreground mb-2 text-xs font-semibold">
                                            Preview
                                        </h3>
                                        <TemplatePdfViewer
                                            pdfUrl={pdfUrl}
                                            slotTargets={fillSlots}
                                            templateName={template.name}
                                            onPdfRefetch={() =>
                                                detailQuery.refetch()
                                            }
                                            className="min-h-0 flex-1"
                                        />
                                    </section>
                                </>
                            )}
                        </>
                    )
                )}
            </div>
        </div>
    );

    return (
        <>
            {isMobile ? (
                <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
                    <SheetContent
                        side="right"
                        showCloseButton={false}
                        className="flex flex-col gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-none"
                    >
                        <SheetTitle className="sr-only">
                            {template?.name ?? "Template details"}
                        </SheetTitle>
                        {panelContent}
                    </SheetContent>
                </Sheet>
            ) : (
                panelContent
            )}

            <RenameTemplateDialog
                open={renameOpen}
                templateId={templateId}
                currentName={template?.name ?? ""}
                onClose={() => setRenameOpen(false)}
            />
            <DeleteTemplateDialog
                open={deleteOpen}
                templateId={templateId}
                templateName={template?.name}
                onClose={() => setDeleteOpen(false)}
                onDeleted={onClose}
            />
            <UploadTemplateDialog
                open={replaceOpen}
                templateId={templateId}
                onClose={() => setReplaceOpen(false)}
            />
        </>
    );
}

const MetaBullet = () => (
    <span aria-hidden className="text-muted-foreground/40">
        ·
    </span>
);

function MetaStrip({
    fileType,
    createdAt,
    boundAgentNames,
}: {
    fileType: OutputTemplateFileType;
    createdAt: string;
    boundAgentNames: string[];
}) {
    const workflowCount = boundAgentNames.length;
    return (
        <div
            data-testid="template-meta-strip"
            className="text-muted-foreground mb-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs"
        >
            {getDocumentTypePill(fileType)}
            <MetaBullet />
            <span title={new Date(createdAt).toLocaleString()}>
                {formatDistanceToNowStrict(new Date(createdAt), {
                    addSuffix: true,
                })}
            </span>
            {workflowCount > 0 && (
                <>
                    <MetaBullet />
                    <span title={boundAgentNames.join(", ")}>
                        Used by {workflowCount}{" "}
                        {workflowCount === 1 ? "workflow" : "workflows"}
                    </span>
                </>
            )}
        </div>
    );
}

function CompileStatusBlock({
    version,
    onRetry,
    onReplace,
    retrying,
}: {
    version: OutputTemplateVersion | null;
    onRetry: () => void;
    onReplace: () => void;
    retrying: boolean;
}) {
    const { user } = useAuth();

    if (!version) {
        return (
            <div
                data-testid="template-compile-status"
                className="mb-5 rounded-lg border border-dashed p-4 text-center"
            >
                <span className="bg-muted text-muted-foreground mx-auto mb-2 flex size-9 items-center justify-center rounded-lg">
                    <FileText className="size-4.5" aria-hidden />
                </span>
                <p className="text-sm font-semibold">
                    This template isn&apos;t ready yet
                </p>
                <p className="text-muted-foreground mt-0.5 text-[0.8rem]">
                    Upload a file to prepare it for filling.
                </p>
            </div>
        );
    }

    const status = version.compile_status;
    if (status === "ready") return null;

    if (status === "pending" || status === "processing") {
        return (
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="template-compile-status"
                className="border-primary/20 bg-primary/5 mb-5 rounded-lg border p-4"
            >
                <div className="flex items-start gap-3">
                    <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                        <Loader2
                            className="size-4.5 animate-spin motion-reduce:animate-none"
                            aria-hidden
                        />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">
                            Getting your template ready…
                        </p>
                        <p className="text-muted-foreground mt-0.5 text-[0.8rem] leading-relaxed">
                            We&apos;re setting up where answers go. This happens
                            once, and only takes a moment.
                        </p>
                    </div>
                </div>
                <div
                    data-testid="template-compile-indeterminate"
                    data-state="indeterminate"
                    aria-hidden="true"
                    className="bg-primary/15 mt-3.5 h-1 overflow-hidden rounded-full"
                >
                    <div
                        data-testid="template-compile-indeterminate-bar"
                        className="bg-primary h-full w-2/5 animate-[template-compile-indeterminate_1.3s_ease-in-out_infinite] rounded-full will-change-transform motion-reduce:animate-none"
                    />
                </div>
            </div>
        );
    }

    if (status === "superseded") {
        return (
            <div
                data-testid="template-compile-status"
                className="bg-muted mb-5 flex items-start gap-3 rounded-lg border p-4"
            >
                <span className="bg-background text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg border">
                    <FileText className="size-4" aria-hidden />
                </span>
                <div>
                    <p className="text-sm font-semibold">
                        Replaced by a newer version
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-[0.8rem] leading-relaxed">
                        This version is kept for reference. The current version
                        is what workflows use.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="template-compile-status"
            className="border-destructive/20 mb-5 rounded-lg border p-4"
        >
            <div className="flex items-start gap-3">
                <span className="bg-destructive/10 text-destructive flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <AlertTriangle className="size-4.5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                        We couldn&apos;t prepare this template
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-[0.8rem] leading-relaxed">
                        We couldn&apos;t read the layout of this file. Try
                        again, or replace it with a cleaner copy.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            data-testid="template-detail-retry"
                            onClick={onRetry}
                            disabled={retrying}
                            aria-busy={retrying}
                        >
                            <RefreshCw
                                className={cn(
                                    "size-3.5",
                                    retrying &&
                                        "animate-spin motion-reduce:animate-none"
                                )}
                                aria-hidden
                            />
                            Try again
                        </Button>
                        <Button variant="outline" size="sm" onClick={onReplace}>
                            <ReplaceIcon className="size-3.5" aria-hidden />
                            Replace file
                        </Button>
                    </div>
                    {user?.is_admin === true &&
                        version.compile_errors &&
                        version.compile_errors.length > 0 && (
                            <details className="group mt-3">
                                <summary className="text-muted-foreground inline-flex cursor-pointer list-none items-center gap-1 text-xs">
                                    <ChevronRight
                                        className="size-3.5 transition-transform group-open:rotate-90 motion-reduce:transition-none"
                                        aria-hidden
                                    />
                                    Technical details
                                </summary>
                                <ul className="text-muted-foreground mt-2 space-y-1 text-xs">
                                    {version.compile_errors.map((err, i) => (
                                        <li
                                            key={`${err.code}-${i}`}
                                            className="bg-muted rounded border-l-2 px-2 py-1"
                                        >
                                            <span className="text-foreground font-medium">
                                                {err.stage}
                                            </span>{" "}
                                            {err.detail}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                </div>
            </div>
        </div>
    );
}

function PanelActions({
    fileType,
    onOpen,
    pending,
    onReplace,
    onDelete,
}: {
    fileType: OutputTemplateFileType;
    onOpen: () => void;
    pending: boolean;
    onReplace: () => void;
    onDelete: () => void;
}) {
    const isSpreadsheet = fileType === "xlsx";
    const actionLabel = isSpreadsheet ? "Download" : "View";

    return (
        <div className="mb-5 flex gap-2">
            <Button
                variant="outline"
                size="sm"
                data-testid="template-download-original"
                className="text-muted-foreground hover:text-foreground flex-1"
                onClick={onOpen}
                disabled={pending}
                aria-label={actionLabel}
                title={actionLabel}
            >
                {pending ? (
                    <Loader2
                        className="size-3.5 animate-spin motion-reduce:animate-none"
                        aria-hidden
                    />
                ) : isSpreadsheet ? (
                    <Download
                        className="size-3.5"
                        strokeWidth={2.2}
                        aria-hidden
                    />
                ) : (
                    <Eye className="size-3.5" strokeWidth={2.2} aria-hidden />
                )}
                <span>{actionLabel}</span>
            </Button>
            <Button
                variant="outline"
                size="sm"
                data-testid="template-replace"
                className="text-muted-foreground hover:text-foreground flex-1"
                onClick={onReplace}
                aria-label="Replace"
                title="Replace"
            >
                <ReplaceIcon
                    className="size-3.5"
                    strokeWidth={2.2}
                    aria-hidden
                />
                <span>Replace</span>
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                data-testid="template-delete"
                className="text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete"
                title="Delete"
            >
                <Trash2 className="size-3.5" strokeWidth={2.2} aria-hidden />
            </Button>
        </div>
    );
}
